// BateCargaConferencia.jsx (FINAL: Supabase, Consulta, Exportação Normal e Aglutinada por Item)
import React, { useState, useCallback, useMemo } from 'react';
import { Upload, Download, X, ChevronDown, ChevronUp, AlertCircle, Package, Truck, Calculator, Save, Search, Calendar, List, Eye, EyeOff, Layers } from 'lucide-react';
import * as XLSX from 'xlsx'; 

// IMPORTANTE: Ajuste o caminho abaixo para onde o seu cliente Supabase foi configurado
import { supabase } from './supabaseClient'; 

/* ------------------ UTILS: XML DOM & Colors ------------------ */

const PRIMARY_COLOR = 'amber-600'; 
const PRIMARY_HOVER = 'amber-700'; 
const SECONDARY_COLOR = 'gray-800'; 

const LOGO_PATH = `${process.env.PUBLIC_URL}/vntech_logo.png`; 

const findFirstText = (node, localName) => {
    if (!node) return '';
    try {
        const nsMatches = node.getElementsByTagNameNS
            ? node.getElementsByTagNameNS('*', localName)
            : [];
        if (nsMatches && nsMatches.length > 0) return nsMatches[0].textContent || '';
    } catch (e) {}
    const all = node.getElementsByTagName('*');
    for (let i = 0; i < all.length; i++) {
        if (all[i].localName === localName) return all[i].textContent || '';
    }
    return '';
};

const findAll = (node, localName) => {
    if (!node) return [];
    try {
        const nsMatches = node.getElementsByTagNameNS
            ? Array.from(node.getElementsByTagNameNS('*', localName))
            : [];
        if (nsMatches.length > 0) return nsMatches;
    } catch (e) {}
    const all = node.getElementsByTagName('*');
    const out = [];
    for (let i = 0; i < all.length; i++) {
        if (all[i].localName === localName) out.push(all[i]);
    }
    return out;
};

const companyColors = {
    SAUDALI: 'from-blue-100 to-indigo-100 border-indigo-600 text-indigo-900',
    AURORA: 'from-green-100 to-emerald-100 border-emerald-600 text-emerald-900',
    BRF: 'from-orange-100 to-amber-100 border-amber-600 text-amber-900',
    default: `from-gray-100 to-gray-200 border-${PRIMARY_COLOR} text-gray-800`, 
};

const getCompanyColorClass = (name) => {
    if (!name) return companyColors.default;
    const k = name.toUpperCase();
    if (k.includes('SAUDALI')) return companyColors.SAUDALI;
    if (k.includes('AURORA')) return companyColors.AURORA;
    if (k.includes('BRF')) return companyColors.BRF;
    return companyColors.default;
};

const getCompanyBorderColor = (name) => {
    const classes = getCompanyColorClass(name).split(' ');
    return classes.find(c => c.startsWith('border-')) || `border-${PRIMARY_COLOR}`;
}

const normalizeNnf = (nnf) => (nnf || '').toString().replace(/\D/g, '');


/* ------------------ COMPONENTE: CALCULADORA RÁPIDA ------------------ */

const QuickCalculator = () => {
    const [display, setDisplay] = useState('0');
    const [currentValue, setCurrentValue] = useState(null);
    const [operator, setOperator] = useState(null);
    const [waitingForOperand, setWaitingForOperand] = useState(false);

    const clear = () => {
        setDisplay('0');
        setCurrentValue(null);
        setOperator(null);
        setWaitingForOperand(false);
    };

    const inputDigit = (digit) => {
        if (waitingForOperand) {
            setDisplay(digit);
            setWaitingForOperand(false);
        } else {
            setDisplay(display === '0' ? digit : display + digit);
        }
    };

    const inputDot = () => {
        if (waitingForOperand) {
            setDisplay('0.');
            setWaitingForOperand(false);
        } else if (display.indexOf('.') === -1) {
            setDisplay(display + '.');
        }
    };

    const performOperation = (nextOperator) => {
        const inputValue = parseFloat(display);

        if (currentValue === null) {
            setCurrentValue(inputValue);
        } else if (!waitingForOperand) {
            const prevValue = currentValue;
            let newValue = prevValue;

            try {
                switch (operator) {
                    case '+': newValue = prevValue + inputValue; break;
                    case '-': newValue = prevValue - inputValue; break;
                    case '*': newValue = prevValue * inputValue; break;
                    case '/': 
                        if (inputValue === 0) {
                            setDisplay('Erro');
                            clear();
                            return;
                        }
                        newValue = prevValue / inputValue; 
                        break;
                    default: break;
                }
                setCurrentValue(newValue);
                setDisplay(String(newValue));
            } catch (e) {
                setDisplay('Erro');
                clear();
                return;
            }
        }
        
        setWaitingForOperand(true);
        setOperator(nextOperator);
    };

    const equals = () => {
        performOperation('=');
        setOperator(null);
        setWaitingForOperand(false);
    }

    const buttonClasses = "p-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-lg font-bold transition duration-100";
    const operatorClasses = "p-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-lg font-bold text-white transition duration-100";

    return (
        <div className="p-4 bg-white border border-gray-200 rounded-xl shadow-lg w-full">
            <h3 className="text-md font-bold text-gray-700 mb-2 flex items-center gap-2">
                <Calculator className='w-4 h-4'/> Calculadora Rápida
            </h3>
            <div className="bg-gray-100 p-2 mb-2 text-right text-2xl font-mono rounded-lg shadow-inner border border-gray-300 overflow-x-auto">
                {display}
            </div>
            <div className="grid grid-cols-4 gap-2">
                <button className={`${buttonClasses} col-span-2 bg-red-400 hover:bg-red-500 text-white`} onClick={clear}>C</button>
                <button className={operatorClasses} onClick={() => performOperation('/')}>/</button>
                <button className={operatorClasses} onClick={() => performOperation('*')}>x</button>
                
                <button className={buttonClasses} onClick={() => inputDigit('7')}>7</button>
                <button className={buttonClasses} onClick={() => inputDigit('8')}>8</button>
                <button className={buttonClasses} onClick={() => inputDigit('9')}>9</button>
                <button className={operatorClasses} onClick={() => performOperation('-')}>-</button>

                <button className={buttonClasses} onClick={() => inputDigit('4')}>4</button>
                <button className={buttonClasses} onClick={() => inputDigit('5')}>5</button>
                <button className={buttonClasses} onClick={() => inputDigit('6')}>6</button>
                <button className={operatorClasses} onClick={() => performOperation('+')}>+</button>
                
                <button className={buttonClasses} onClick={() => inputDigit('1')}>1</button>
                <button className={buttonClasses} onClick={() => inputDigit('2')}>2</button>
                <button className={buttonClasses} onClick={() => inputDigit('3')}>3</button>
                <button className={`${operatorClasses} row-span-2`} onClick={equals}>=</button>

                <button className={`${buttonClasses} col-span-2`} onClick={() => inputDigit('0')}>0</button>
                <button className={buttonClasses} onClick={inputDot}>.</button>
            </div>
        </div>
    );
};


/* ------------------ COMPONENTE PRINCIPAL ------------------ */

export default function BateCargaConferencia() {
    // Estado de Navegação da Tela
    const [view, setView] = useState('gerar'); // 'gerar' ou 'consultar'

    // Estados do Modo Gerar
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [fileNames, setFileNames] = useState([]);
    const [placaDataMap, setPlacaDataMap] = useState(new Map());
    const [logisticaFileName, setLogisticaFileName] = useState('');
    const [loadingProgress, setLoadingProgress] = useState(0); 
    const [isSaving, setIsSaving] = useState(false); 

    // Estados do Modo Consultar
    const [consultaDataStr, setConsultaDataStr] = useState('');
    const [consultaResultados, setConsultaResultados] = useState([]);
    const [consultaLoading, setConsultaLoading] = useState(false);
    
    // Estados do Modo Consultar - Detalhes da Nota
    const [expandedNfId, setExpandedNfId] = useState(null);
    const [nfItems, setNfItems] = useState([]);
    const [loadingItems, setLoadingItems] = useState(false);

    const totalItemsToExport = useMemo(() => {
        return groups.reduce((acc, g) => acc + g.totals.totalItems, 0);
    }, [groups]);

    const clearAll = useCallback(() => {
        setGroups([]);
        setFileNames([]);
        setError(null);
        setLoading(false);
        setPlacaDataMap(new Map());
        setLogisticaFileName('');
        setLoadingProgress(0); 
    }, []);

    // -------------------------------------------------------------------
    // CORE: Função para Salvar no Supabase (Com Validação de Duplicidade)
    // -------------------------------------------------------------------
    const handleValidarESalvar = async () => {
        if (groups.length === 0) {
            setError('Nenhum dado importado para salvar.');
            return;
        }

        setIsSaving(true);
        setError(null);

        try {
            const todasNotas = [];
            groups.forEach(g => {
                g.notes.forEach(n => {
                    if (n.chNFe) {
                        todasNotas.push({ group: g, note: n });
                    }
                });
            });

            if (todasNotas.length === 0) {
                throw new Error('Não foi possível encontrar a Chave da NF-e nos arquivos importados.');
            }

            const chavesParaValidar = todasNotas.map(item => item.note.chNFe);

            const { data: chavesExistentes, error: checkError } = await supabase
                .from('bate_carga_nf')
                .select('chave_nfe')
                .in('chave_nfe', chavesParaValidar);

            if (checkError) throw checkError;

            if (chavesExistentes && chavesExistentes.length > 0) {
                const listaDuplicadas = chavesExistentes.map(c => c.chave_nfe).join(',\n');
                throw new Error(`Validação falhou. As seguintes notas já foram salvas anteriormente no banco de dados:\n\n${listaDuplicadas}\n\nOperação cancelada.`);
            }

            for (const item of todasNotas) {
                const { group, note } = item;
                
                const { data: notaInserida, error: insertNfError } = await supabase
                    .from('bate_carga_nf')
                    .insert([{
                        chave_nfe: note.chNFe,
                        numero_nf: note.nNF,
                        grupo_emitente: group.emitFant || group.emitName,
                        cidade: group.city,
                        destinatario: note.destName,
                        placa_veiculo: note.Placa,
                        peso_bruto_total: note.pesoB || 0,
                        volumes: note.qVol || 0,
                        observacao: note.Observacao
                    }])
                    .select()
                    .single();

                if (insertNfError) throw insertNfError;

                if (note.items && note.items.length > 0) {
                    const itensParaInserir = note.items.map(it => ({
                        id_nota: notaInserida.id,
                        // Aqui já limpamos o cProd também ao salvar no banco
                        codigo_produto: (it.cProd || '').toString().replace(/\D/g, ''),
                        descricao_produto: it.xProd,
                        qtd_xml: parseFloat((it.qCom || '').toString().replace(',', '.')) || 0,
                        qtd_conferida: parseFloat((it.Quantidade_Conferida || '').toString().replace(',', '.')) || 0,
                        qtde_aux: it.infAdProd,
                        status_conferencia: it.Status || 'PENDENTE'
                    }));

                    const { error: insertItemsError } = await supabase
                        .from('bate_carga_item')
                        .insert(itensParaInserir);

                    if (insertItemsError) throw insertItemsError;
                }
            }

            alert('Bate Carga validado e salvo com sucesso no Banco de Dados!');
            
        } catch (err) {
            console.error('Erro ao Salvar no BD:', err);
            setError(err.message);
        } finally {
            setIsSaving(false);
        }
    };

    // -------------------------------------------------------------------
    // CORE: Função para Consultar Bate Carga no Supabase (Por Data)
    // -------------------------------------------------------------------
    const handleConsultar = async () => {
        if (!consultaDataStr) {
            setError('Por favor, selecione uma data para a consulta.');
            return;
        }

        setConsultaLoading(true);
        setError(null);
        setConsultaResultados([]);
        setExpandedNfId(null); 

        try {
            const dataInicio = new Date(`${consultaDataStr}T00:00:00`).toISOString();
            const dataFim = new Date(`${consultaDataStr}T23:59:59.999`).toISOString();

            const { data, error: fetchError } = await supabase
                .from('bate_carga_nf')
                .select('*')
                .gte('data_importacao', dataInicio)
                .lte('data_importacao', dataFim)
                .order('data_importacao', { ascending: false });

            if (fetchError) throw fetchError;
            
            setConsultaResultados(data || []);
            
            if (!data || data.length === 0) {
                setError('Nenhum Bate Carga encontrado para esta data.');
            }

        } catch (err) {
            console.error('Erro na Consulta:', err);
            setError(err.message);
        } finally {
            setConsultaLoading(false);
        }
    };

    const toggleNfDetails = async (idNota) => {
        if (expandedNfId === idNota) {
            setExpandedNfId(null);
            setNfItems([]);
            return;
        }

        setExpandedNfId(idNota);
        setLoadingItems(true);
        try {
            const { data, error } = await supabase
                .from('bate_carga_item')
                .select('*')
                .eq('id_nota', idNota)
                .order('codigo_produto', { ascending: true });

            if (error) throw error;
            setNfItems(data || []);
        } catch (err) {
            console.error('Erro ao buscar itens da nota:', err);
            alert('Erro ao carregar os itens da nota.');
            setExpandedNfId(null);
        } finally {
            setLoadingItems(false);
        }
    };


    const mapPlacaToNotes = useCallback((notes, placaMap) => {
        return notes.map(note => {
            const cleanedNnf = normalizeNnf(note.nNF);
            const placaInfo = placaMap.get(cleanedNnf);
            return {
                ...note,
                Placa: placaInfo ? placaInfo.Placa : note.Placa || '',
            };
        });
    }, []);

    const simulateLoad = async (callback, finalProgress = 100) => {
        setLoading(true);
        setLoadingProgress(0);

        const interval = setInterval(() => {
            setLoadingProgress(prev => {
                if (prev >= 90) return prev; 
                return prev + 10;
            });
        }, 200);

        await new Promise(resolve => setTimeout(resolve, 2000));
        
        clearInterval(interval);
        setLoadingProgress(finalProgress);
        
        try {
            await callback();
        } catch (e) {
            throw e;
        } finally {
            if (finalProgress === 100) {
                 setLoading(false);
                 setLoadingProgress(0);
            }
        }
    };

    const processLogisticaData = (raw_data, fileName, placaMap) => {
        const rawHeaders = raw_data[0] || [];
        const headers = rawHeaders.map(h => 
            (h || '').toString().toUpperCase().trim()
             .replace(/"/g, '')
             .replace(/[^A-Z0-9\s]/g, '') 
             .replace(/\s+/g, ' ') 
        );

        let nNFIndex = -1;
        let placaIndex = -1;
        
        placaIndex = headers.findIndex(h => h.includes('PLACA') || h.includes('VEICULO'));
        nNFIndex = headers.findIndex(h => h.includes('NUMERO NF') || h.includes('N NOTA') || h.includes('NNF') || h.includes('NF'));
        
        if (placaIndex === -1 && rawHeaders[0] && rawHeaders[0].toUpperCase().includes('PLACA')) placaIndex = 0;
        if (nNFIndex === -1 && rawHeaders[1] && rawHeaders[1].toUpperCase().includes('NF')) nNFIndex = 1;

        if (nNFIndex === -1 || placaIndex === -1) {
            throw new Error(`Não foi possível encontrar as colunas "PLACA" e "Número NF" (ou NNF/N_NOTA). Cabeçalhos lidos: [${rawHeaders.join(', ')}]`);
        }

        const newPlacaMap = new Map();
        for (let i = 1; i < raw_data.length; i++) {
            const values = raw_data[i] || [];
            
            const nNF = (values[nNFIndex] || '').toString().trim();
            const placa = (values[placaIndex] || '').toString().trim();
            
            if (nNF && placa) {
                const cleanedNnf = normalizeNnf(nNF); 
                newPlacaMap.set(cleanedNnf, { Placa: placa.toUpperCase() });
            }
        }

        if (newPlacaMap.size === 0) {
            throw new Error('Nenhuma linha de dados válida (NF e Placa) foi encontrada na planilha.');
        }

        return newPlacaMap;
    }

    const handleLogisticaUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (groups.length === 0) {
            setError('Por favor, importe os arquivos XML primeiro para que o mapeamento possa ser aplicado.');
            e.target.value = null;
            return;
        }

        setLogisticaFileName(file.name);
        setError(null);
        e.target.value = null;

        const reader = new FileReader();
        const extension = file.name.split('.').pop().toLowerCase();
        
        const attemptBinaryRead = (arrayBuffer) => {
            try {
                const workbook = XLSX.read(arrayBuffer, { 
                    type: 'array',
                    cellText: false, 
                    cellDates: true 
                });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                
                if (!worksheet) {
                     throw new Error(`A aba "${sheetName}" não foi encontrada.`);
                }

                const raw_data = XLSX.utils.sheet_to_json(worksheet, { 
                    header: 1,
                    defval: "" 
                });
                
                if (raw_data.length <= 1) {
                    throw new Error('Planilha lida, mas sem dados na Linha 2.');
                }
                
                return processLogisticaData(raw_data, file.name, placaDataMap);

            } catch (err) {
                 if (extension === 'xls' || extension === 'csv') {
                     throw new Error('BINÁRIO_FALHA'); 
                 }
                 throw err; 
            }
        };

        const attemptTextRead = (text) => {
            const workbook = XLSX.read(text, { 
                type: 'string',
                cellText: false, 
                cellDates: true 
            });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            
            const raw_data = XLSX.utils.sheet_to_json(worksheet, { 
                header: 1,
                defval: "" 
            });

            if (raw_data.length <= 1) {
                throw new Error('Leitura de Texto falhou ao encontrar dados.');
            }
            
            return processLogisticaData(raw_data, file.name, placaDataMap);
        };
        
        reader.onload = async (event) => {
            try {
                await simulateLoad(async () => {
                    let newPlacaMap;

                    if (extension === 'xlsx' || extension === 'xls' || extension === 'csv') {
                        try {
                            newPlacaMap = attemptBinaryRead(event.target.result);
                        } catch (e) {
                            if (e.message !== 'BINÁRIO_FALHA') throw e; 
                            
                            const textReader = new FileReader();
                            await new Promise((resolve, reject) => {
                                textReader.onload = (e2) => {
                                    try {
                                        newPlacaMap = attemptTextRead(e2.target.result);
                                        resolve();
                                    } catch (e3) {
                                        reject(`Erro ao processar ${file.name} (Texto): ${e3.message}`);
                                    }
                                };
                                textReader.onerror = () => reject(`Erro de leitura no arquivo ${file.name}`);
                                textReader.readAsText(file, 'UTF-8');
                            });
                        }
                    } else {
                        throw new Error('Extensão de arquivo não suportada.');
                    }
                    
                    setPlacaDataMap(newPlacaMap);
                    setGroups((prevGroups) => prevGroups.map(g => ({ ...g, notes: mapPlacaToNotes(g.notes, newPlacaMap), })));
                    
                    setLoading(false);
                    setLoadingProgress(0);
                }); 
            } catch (err) {
                console.error('Erro geral no upload da planilha', err);
                setError(typeof err === 'string' ? err : `Erro ao processar ${file.name}: ${err.message}`);
                setPlacaDataMap(new Map());
                setLoading(false);
                setLoadingProgress(0); 
            }
        };

        reader.readAsArrayBuffer(file);
    };
    
    const parseNFDoc = (xmlDoc) => {
        const infNFe = findAll(xmlDoc, 'infNFe')[0] || xmlDoc;
        const ideNode = findAll(infNFe, 'ide')[0] || {};
        const nNF = findFirstText(ideNode, 'nNF') || findFirstText(xmlDoc, 'nNF');
        const dhEmiRaw = findFirstText(ideNode, 'dhEmi') || findFirstText(infNFe, 'dhEmi') || '';
        const dhEmi = dhEmiRaw ? dhEmiRaw.substring(0, 10).replace(/(\d{4})-(\d{2})-(\d{2})/, '$3/$2/$1') : '';

        const emitNode = findAll(infNFe, 'emit')[0];
        const emitName = findFirstText(emitNode, 'xNome') || '';
        const emitFant = findFirstText(emitNode, 'xFant') || emitName || '';

        const destNode = findAll(infNFe, 'dest')[0];
        const destName = findFirstText(destNode, 'xNome') || '';
        const enderDest = findAll(destNode, 'enderDest')[0];
        const destCity = findFirstText(enderDest, 'xMun') || '';
        const destUF = findFirstText(enderDest, 'UF') || '';

        const transporta = findAll(infNFe, 'transporta')[0];
        const transportName = findFirstText(transporta, 'xNome') || ''; 
        
        const volNode = findAll(infNFe, 'vol')[0] || findAll(findAll(infNFe, 'transp')[0], 'vol')[0] || null;
        const qVol = volNode ? (findFirstText(volNode, 'qVol') || '') : '';
        const pesoBNota = volNode ? (findFirstText(volNode, 'pesoB') || '') : ''; 
        const pesoLNota = volNode ? (findFirstText(volNode, 'pesoL') || '') : ''; 

        const icmstot = findAll(infNFe, 'ICMSTot')[0] || findAll(infNFe, 'total')[0];
        const vNF = icmstot ? (findFirstText(icmstot, 'vNF') || '') : findFirstText(infNFe, 'vNF') || '';

        const infAdicNode = findAll(xmlDoc, 'infAdic')[0];
        const infCplText = infAdicNode ? findFirstText(infAdicNode, 'infCpl') : '';
        
        let sequenciaEntrega = '';
        if (infCplText) {
            const match = infCplText.match(/SEQUENCIA ENTREGA:\s*(\d+)/i);
            sequenciaEntrega = match ? match[1].trim() : '';
        }

        const detNodes = findAll(infNFe, 'det');
        const items = detNodes.map((det) => {
            const prod = findAll(det, 'prod')[0] || det;
            const cProd = findFirstText(prod, 'cProd') || '';
            const xProd = findFirstText(prod, 'xProd') || '';
            const qCom = findFirstText(prod, 'qCom') || findFirstText(prod, 'qtd') || '';
            const uCom = findFirstText(prod, 'uCom') || '';
            const vUnCom = findFirstText(prod, 'vUnCom') || '';
            const vProd = findFirstText(prod, 'vProd') || '';
            
            const pesoBItem = findFirstText(prod, 'pesoB') || ''; 
            const pesoLItem = findFirstText(prod, 'pesoL') || ''; 
            
            const infAdProd = findFirstText(det, 'infAdProd') || '';

            return {
                cProd, xProd, qCom, uCom, vUnCom, vProd, infAdProd,
                pesoBItem: parseFloat(pesoBItem) || 0, 
                pesoLItem: parseFloat(pesoLItem) || 0, 
                Quantidade_Conferida: '',
                Status: '',
            };
        });

        const prot = findAll(xmlDoc, 'protNFe')[0] || findAll(xmlDoc, 'protNFe')[0];
        const chNFe = prot ? (findFirstText(prot, 'chNFe') || '') : '';

        return {
            chNFe, nNF, dhEmi, emitName, emitFant, destName, destCity, destUF, transportName,
            qVol: parseFloat(qVol) || 0,
            pesoB: parseFloat(pesoBNota) || 0, 
            pesoL: parseFloat(pesoLNota) || 0, 
            vNF: parseFloat(vNF) || 0,
            items,
            rawXml: xmlDoc,
            Observacao: '', 
            Placa: '',
            sequenciaEntrega, 
        };
    };

    const handleXMLUpload = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        setError(null);
        setFileNames(files.map((f) => f.name));

        e.target.value = null;

        const notes = [];
        
        simulateLoad(async () => {
            await Promise.all(files.map(file => new Promise(resolve => {
                const reader = new FileReader();
                reader.onload = (ev) => {
                    try {
                        const xmlString = ev.target.result;
                        const parser = new DOMParser();
                        const xmlDoc = parser.parseFromString(xmlString, 'application/xml');

                        if (xmlDoc.getElementsByTagName('parsererror').length > 0) {
                            throw new Error(`Parser error no arquivo ${file.name}`);
                        }
                        let note = parseNFDoc(xmlDoc);
                        
                        if (note.sequenciaEntrega) {
                            const prefix = `Seq. Entrega: ${note.sequenciaEntrega}`;
                            note.Observacao = prefix;
                        }
                        
                        const cleanedNnf = normalizeNnf(note.nNF);
                        const placaInfo = placaDataMap.get(cleanedNnf);
                        if (placaInfo) {
                            note.Placa = placaInfo.Placa;
                        }
                        
                        if (note && note.items && note.items.length > 0) notes.push(note);
                    } catch (err) {
                        console.error('Erro parse XML', file.name, err);
                        setError((prev) => prev ? `${prev}\nErro em ${file.name}` : `Erro em ${file.name}`);
                    } finally {
                        resolve();
                    }
                };
                reader.readAsText(file, 'UTF-8');
            })));

            if (notes.length === 0) {
                setError((prev) => prev ? prev : 'Nenhum item válido encontrado.');
            }
            const grouped = [];
            notes.forEach((note) => {
                const key = `${note.emitFant}||${note.destCity}`;
                const idx = grouped.findIndex((g) => g.key === key);
                if (idx === -1) {
                    grouped.push({
                        key,
                        emitFant: note.emitFant,
                        emitName: note.emitName,
                        city: note.destCity,
                        uf: note.destUF,
                        notes: [{ ...note, open: false }], 
                        totals: {
                            totalPesoB: note.pesoB || 0,
                            totalQVol: note.qVol || 0, 
                            totalVNF: note.vNF || 0,
                            totalNotes: 1,
                            totalItems: note.items.length,
                        },
                        open: true,
                    });
                } else {
                    const g = grouped[idx];
                    g.notes.push({ ...note, open: false }); 
                    g.totals.totalPesoB += note.pesoB || 0;
                    g.totals.totalQVol += note.qVol || 0; 
                    g.totals.totalVNF += note.vNF || 0;
                    g.totals.totalNotes += 1;
                    g.totals.totalItems += note.items.length;
                }
            });
            setGroups(grouped);
            setLoading(false);
            setLoadingProgress(0);
        });
    };

    const toggleGroup = (key) => {
        setGroups((prev) =>
            prev.map((g) => (g.key === key ? { ...g, open: !g.open } : g))
        );
    };

    const toggleNoteOpen = (groupKey, noteIndex) => {
        setGroups((prev) =>
            prev.map((g) =>
                g.key === groupKey
                    ? {
                        ...g,
                        notes: g.notes.map((n, idx) =>
                            idx === noteIndex ? { ...n, open: !n.open } : n
                        ),
                    }
                    : g
            )
        );
    };

    const handleObservacaoChange = (groupKey, noteIndex, value) => {
        setGroups((prev) =>
            prev.map((g) => {
                if (g.key !== groupKey) return g;
                const notes = g.notes.map((n, ni) => {
                    if (ni !== noteIndex) return n;
                    return { ...n, Observacao: value };
                });
                return { ...g, notes };
            })
        );
    };

    const handleQuantidadeChange = (groupKey, noteIndex, itemIndex, value) => {
        setGroups((prev) =>
            prev.map((g) => {
                if (g.key !== groupKey) return g;
                const notes = g.notes.map((n, ni) => {
                    if (ni !== noteIndex) return n;
                    const items = n.items.map((it, ii) => {
                        if (ii !== itemIndex) return it;
                        const updated = { ...it, Quantidade_Conferida: value };
                        const expected = parseFloat((it.qCom || '').toString().replace(',', '.')) || 0;
                        const conf = parseFloat((value || '').toString().replace(',', '.')) || 0;
                        if (conf === expected && expected > 0) {
                            updated.Status = 'OK';
                        } else if (conf === 0 || value === '') {
                            updated.Status = '';
                        } else {
                            updated.Status = 'Divergente';
                        }
                        return updated;
                    });
                    return { ...n, items }; 
                });
                return { ...g, notes };
            })
        );
    };
    
    const generateConferenceReport = () => {
        if (groups.length === 0) {
            setError('Nenhum dado para gerar relatório.');
            return;
        }

        const reportContent = groups.map(g => {
            const groupHeader = `
                <h2>GRUPO: ${g.emitFant} / ${g.city} (${g.totals.totalNotes} NF)</h2>
                <p>Peso Bruto Total: ${Number(g.totals.totalPesoB).toLocaleString('pt-BR', { maximumFractionDigits: 3 })} kg | Volumes: ${g.totals.totalQVol}</p>
                <hr style="margin: 10px 0; border: 1px dashed #ccc;">
            `;

            const notesContent = g.notes.map((n, noteIndex) => {
                const itemsList = n.items.map((it, itemIndex) => {
                    // LIMPEZA CPROD NA TELA DE IMPRESSÃO TAMBÉM (OPCIONAL)
                    const cProdClean = (it.cProd || '').toString().replace(/\D/g, '');
                    return `
                    <tr style="border-bottom: 1px solid #eee;">
                        <td style="padding: 4px 8px; text-align: left;">${cProdClean}</td>
                        <td style="padding: 4px 8px; text-align: left;">${it.xProd}</td>
                        <td style="padding: 4px 8px; text-align: center;">${it.qCom} ${it.uCom}</td>
                        <td style="padding: 4px 8px; text-align: center; font-weight: bold; background-color: #f0f0f0;">${it.Quantidade_Conferida || '_______'}</td>
                        <td style="padding: 4px 8px; text-align: center; color: blue;">${it.infAdProd || '---'}</td>
                        <td style="padding: 4px 8px; text-align: left; font-size: 10px;">___________________________</td>
                    </tr>
                `}).join('');

                return `
                    <div style="margin-bottom: 20px; border: 1px solid #ddd; padding: 10px; border-radius: 5px;">
                        <h3>NF: ${n.nNF} (${n.dhEmi})</h3>
                        <p style="font-size: 12px; margin: 5px 0;">
                            Destinatário: ${n.destName} | Placa: <span style="font-weight: bold;">${n.Placa || 'NÃO INFORMADA'}</span>
                        </p>
                        <table style="width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 10px;">
                            <thead style="background-color: #e8e8e8;">
                                <tr>
                                    <th style="padding: 4px 8px; text-align: left;">CÓDIGO</th>
                                    <th style="padding: 4px 8px; text-align: left;">PRODUTO</th>
                                    <th style="padding: 4px 8px; text-align: center;">QTD NF</th>
                                    <th style="padding: 4px 8px; text-align: center;">QTD CONF.</th>
                                    <th style="padding: 4px 8px; text-align: center;">QTDE AUX.</th>
                                    <th style="padding: 4px 8px; text-align: left;">OBS/LACRE/TEMP.</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${itemsList}
                            </tbody>
                        </table>
                        <p style="font-size: 12px; margin-top: 10px;">
                            **Observação da Conferência (Nota ${n.nNF}): ${n.Observacao || 'Sem observação'}**
                        </p>
                    </div>
                `;
            }).join('');

            return `<div style="padding-bottom: 30px; page-break-after: always;">${groupHeader}${notesContent}</div>`;
        }).join('');

        const htmlContent = `
            <html>
            <head>
                <title>Relatório de Conferência de Carga</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 20px; }
                    h1 { text-align: center; color: #1e40af; }
                    h2 { margin-top: 25px; border-bottom: 2px solid #1e40af; padding-bottom: 5px; color: #1e40af; }
                    hr { border: none; border-top: 1px dashed #ccc; margin: 15px 0; }
                    @media print {
                        @page { margin: 1cm; }
                        div[style*="page-break-after"] { page-break-after: always !important; }
                    }
                </style>
            </head>
            <body>
                <h1>RELATÓRIO DE CONFERÊNCIA DE CARGA - FRIOLOG</h1>
                <p style="text-align: right; font-size: 10px;">Data de Emissão: ${new Date().toLocaleDateString('pt-BR')} ${new Date().toLocaleTimeString('pt-BR')}</p>
                ${reportContent}
            </body>
            </html>
        `;

        const printWindow = window.open('', '', 'height=800,width=1000');
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    };

    // -------------------------------------------------------------------
    // EXPORTAÇÃO 1: CSV Normal (Mantém 1 linha por item da nota)
    // -------------------------------------------------------------------
    const exportToCSV = () => {
        if (!groups || groups.length === 0) {
            setError('Nenhum dado para exportar.');
            return;
        }

        const headers = [
            'Grupo_Emitente', 'Cidade', 'nNF', 
            'Destinatario_Nota',
            'Placa_Veiculo', 
            'PesoB_Nota_Total', 'QVol', 
            'cProd', 'xProd', 
            'Qtd_Item_XML', 
            'Qtde_Aux',
            'Observacao', 
        ];

        let csv = '\ufeff' + headers.join(';') + '\n';
        groups.forEach((g) => {
            g.notes.forEach((n) => {
                n.items.forEach((it) => {
                    const formatValue = (val) => {
                        if (val === null || val === undefined) return '""';
                        let str = val.toString();
                        str = str.replace(/\n/g, ' ').replace(/"/g, '""');
                        
                        if (typeof val === 'number' || (typeof val === 'string' && val.trim() !== '' && !isNaN(Number(val)))) {
                            try {
                                const num = Number(str);
                                str = num.toFixed(4).replace('.', ','); 
                            } catch (e) {
                                str = val.toString().replace(/\n/g, ' ').replace(/"/g, '""');
                            }
                        }
                        return `"${str}"`;
                    };

                    // Limpa o código do produto de qualquer caractere não numérico
                    const cleanCProd = (it.cProd || '').toString().replace(/\D/g, '');

                    const row = [
                        formatValue(g.emitFant), formatValue(g.city || ''), formatValue(n.nNF || ''),
                        formatValue(n.destName || ''), 
                        formatValue(n.Placa || ''), 
                        formatValue(n.pesoB || ''), 
                        formatValue(n.qVol || ''), 
                        // Ignora o formatValue padrão para o código do produto para evitar casas decimais indesejadas (,0000)
                        `"${cleanCProd}"`,
                        formatValue(it.xProd || ''), 
                        formatValue(it.qCom || ''), 
                        formatValue(it.infAdProd || ''),
                        formatValue(n.Observacao || ''),
                    ];
                    csv += row.join(';') + '\n';
                });
            });
        });

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bate_carga_export_${new Date().getTime()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // -------------------------------------------------------------------
    // EXPORTAÇÃO 2: CSV Aglutinado (Soma das quantidades do mesmo item)
    // -------------------------------------------------------------------
    const exportToAggregatedCSV = () => {
        if (!groups || groups.length === 0) {
            setError('Nenhum dado para exportar.');
            return;
        }

        // Usamos um Map para acumular as quantidades agrupadas pelo código limpo do produto
        const aggregatedMap = new Map();

        groups.forEach((g) => {
            g.notes.forEach((n) => {
                n.items.forEach((it) => {
                    // Limpa o código para usar como chave do agrupamento
                    const cleanCProd = (it.cProd || '').toString().replace(/\D/g, '');
                    const key = cleanCProd || 'SEM_CODIGO';

                    const qtdXml = parseFloat((it.qCom || '').toString().replace(',', '.')) || 0;
                    const qtdConf = parseFloat((it.Quantidade_Conferida || '').toString().replace(',', '.')) || 0;

                    if (aggregatedMap.has(key)) {
                        const existing = aggregatedMap.get(key);
                        existing.qXmlTotal += qtdXml;
                        existing.qConfTotal += qtdConf;
                    } else {
                        aggregatedMap.set(key, {
                            cProd: cleanCProd,
                            xProd: it.xProd || '',
                            qXmlTotal: qtdXml,
                            qConfTotal: qtdConf
                        });
                    }
                });
            });
        });

        const headers = ['Codigo_Produto', 'Descricao', 'Quantidade_Total_XML', 'Quantidade_Total_Conferida'];
        let csv = '\ufeff' + headers.join(';') + '\n';

        aggregatedMap.forEach((data) => {
            const formatValue = (val) => {
                if (val === null || val === undefined) return '""';
                let str = val.toString();
                str = str.replace(/\n/g, ' ').replace(/"/g, '""');
                
                if (typeof val === 'number' || (typeof val === 'string' && val.trim() !== '' && !isNaN(Number(val)))) {
                    try {
                        const num = Number(str);
                        str = num.toFixed(4).replace('.', ','); 
                    } catch (e) {
                        str = val.toString().replace(/\n/g, ' ').replace(/"/g, '""');
                    }
                }
                return `"${str}"`;
            };

            const row = [
                `"${data.cProd}"`, // Código já vem limpo e sem formatação decimal
                formatValue(data.xProd),
                formatValue(data.qXmlTotal),
                formatValue(data.qConfTotal)
            ];
            csv += row.join(';') + '\n';
        });

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bate_carga_aglutinado_${new Date().getTime()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    /* ------------------ RENDER (UI) ------------------ */
    
    const LogoComponent = () => (
        <img 
            src={LOGO_PATH} 
            alt="VNTECH" 
            className="h-8 w-auto" 
        />
    ); 

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            {/* HEADER com Navegação de Abas */}
            <header className="bg-white shadow-xl p-4 flex-shrink-0 border-b border-gray-200">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <LogoComponent />
                        <h1 className={`text-xl font-extrabold text-${SECONDARY_COLOR} tracking-wide`}>
                            | <span className={`text-${PRIMARY_COLOR}`}>CONFERÊNCIA DE CARGA</span>
                        </h1>
                    </div>
                    {/* Botões de Navegação (Gerar vs Consultar) */}
                    <div className="flex bg-gray-100 p-1 rounded-full shadow-inner">
                        <button 
                            onClick={() => { setView('gerar'); setError(null); }}
                            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition duration-200 ${view === 'gerar' ? `bg-${PRIMARY_COLOR} text-white shadow` : 'text-gray-600 hover:text-gray-800'}`}
                        >
                            <Package className="w-4 h-4" /> Gerar Bate Carga
                        </button>
                        <button 
                            onClick={() => { setView('consultar'); setError(null); }}
                            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition duration-200 ${view === 'consultar' ? `bg-${PRIMARY_COLOR} text-white shadow` : 'text-gray-600 hover:text-gray-800'}`}
                        >
                            <Search className="w-4 h-4" /> Painel de Consulta
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto p-4 md:p-8 flex-grow w-full">
                
                {/* -------------------- MODO: GERAR BATE CARGA -------------------- */}
                {view === 'gerar' && (
                    <>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                            {/* Card de Upload e Ações */}
                            <div className="lg:col-span-2 bg-white rounded-xl shadow-2xl p-6 border border-gray-100 h-fit">
                                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                                    <div className='flex items-center gap-4'>
                                        <div className={`p-3 bg-white rounded-full border border-${PRIMARY_COLOR} flex-shrink-0`}>
                                            <Package className={`w-6 h-6 text-${PRIMARY_COLOR}`} />
                                        </div>
                                        <div>
                                            <h2 className="text-2xl font-bold text-gray-800">Conferência de Carga</h2>
                                            <p className="text-sm text-gray-600 mt-1">
                                                Importe arquivos XML (NF-e) e a planilha de logística (XLS/XLSX/CSV).
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-3 mt-4 md:mt-0 md:justify-end">
                                        
                                        <button
                                            onClick={handleValidarESalvar}
                                            className={`flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-full text-sm font-medium transition duration-150 ease-in-out shadow-lg shadow-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed`}
                                            disabled={groups.length === 0 || isSaving}
                                        >
                                            <Save className="w-4 h-4" />
                                            {isSaving ? 'Salvando...' : 'Validar e Salvar BD'}
                                        </button>

                                        <button
                                            onClick={generateConferenceReport}
                                            className={`flex items-center gap-2 bg-${PRIMARY_COLOR} hover:bg-${PRIMARY_HOVER} text-white px-4 py-2 rounded-full text-sm font-medium transition duration-150 ease-in-out shadow-lg shadow-amber-200 disabled:opacity-50 disabled:cursor-not-allowed`}
                                            disabled={groups.length === 0}
                                        >
                                            <List className="w-4 h-4" />
                                            Gerar Relatório
                                        </button>

                                        {/* NOVO BOTÃO: Exportar Aglutinado */}
                                        <button
                                            onClick={exportToAggregatedCSV}
                                            className={`flex items-center gap-2 bg-${PRIMARY_COLOR} hover:bg-${PRIMARY_HOVER} text-white px-4 py-2 rounded-full text-sm font-medium transition duration-150 ease-in-out shadow-lg shadow-amber-200 disabled:opacity-50 disabled:cursor-not-allowed`}
                                            disabled={groups.length === 0}
                                            title="Soma os totais de itens iguais no arquivo todo"
                                        >
                                            <Layers className="w-4 h-4" />
                                            Exportar Aglutinado
                                        </button>
                                        
                                        <button
                                            onClick={exportToCSV}
                                            className={`flex items-center gap-2 bg-${PRIMARY_COLOR} hover:bg-${PRIMARY_HOVER} text-white px-4 py-2 rounded-full text-sm font-medium transition duration-150 ease-in-out shadow-lg shadow-amber-200 disabled:opacity-50 disabled:cursor-not-allowed`}
                                            disabled={groups.length === 0}
                                        >
                                            <Download className="w-4 h-4" />
                                            Exportar CSV Normal
                                        </button>
                                        
                                        <button
                                            onClick={clearAll}
                                            className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-full text-sm font-medium transition duration-150 ease-in-out shadow-lg shadow-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                            disabled={groups.length === 0 && fileNames.length === 0 && !error && !loading}
                                        >
                                            <X className="w-4 h-4" />
                                            Limpar
                                        </button>
                                    </div>
                                </div>

                                {/* Bloco de Erro/Progresso */}
                                <div className="mt-6">
                                    {loading && (
                                        <div className="mt-4">
                                            <div className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                                                <div className={`w-3 h-3 bg-${PRIMARY_COLOR} rounded-full animate-pulse`}></div>
                                                Processando arquivos...
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                                <div 
                                                    className={`h-2.5 rounded-full bg-${PRIMARY_COLOR} transition-all duration-300 ease-in-out`} 
                                                    style={{ width: `${loadingProgress}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    )}

                                    {error && (
                                        <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-400 text-red-800 rounded-lg flex items-start gap-2">
                                            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                            <span className='whitespace-pre-line text-sm'>{error}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="mt-6 flex flex-col lg:flex-row items-center gap-4">
                                    <label className={`cursor-pointer w-full lg:w-1/2`}>
                                        <div className={`border-2 border-dashed rounded-lg p-3 flex items-center gap-4 min-w-[300px] transition duration-200
                                                ${fileNames.length > 0 ? 'bg-green-50 border-green-400 text-green-700' : `bg-white border-${PRIMARY_COLOR} hover:bg-gray-50`}`}>
                                            <Upload className={`w-5 h-5 text-${PRIMARY_COLOR} flex-shrink-0`} />
                                            <div>
                                                <div className="font-semibold text-sm">
                                                    {fileNames.length > 0 ? `${fileNames.length} arquivo(s) XML carregado(s)` : '1. Carregar Notas Fiscais (XML)'}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    Aceita múltiplos arquivos .xml
                                                </div>
                                            </div>
                                        </div>
                                        <input
                                            type="file"
                                            accept=".xml"
                                            multiple
                                            onChange={handleXMLUpload}
                                            className="hidden"
                                        />
                                    </label>

                                    <label className={`cursor-pointer w-full lg:w-1/2`}>
                                        <div className={`border-2 border-dashed rounded-lg p-3 flex items-center gap-4 min-w-[300px] transition duration-200
                                                ${placaDataMap.size > 0 ? `bg-amber-50 border-amber-400 text-amber-700` : `bg-white border-${PRIMARY_COLOR} hover:bg-gray-50`}
                                                ${groups.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                            <Truck className={`w-5 h-5 text-${PRIMARY_COLOR} flex-shrink-0`} />
                                            <div>
                                                <div className="font-semibold text-sm">
                                                    {logisticaFileName ? `2. Planilha de Logística: ${logisticaFileName} (${placaDataMap.size} placas mapeadas)` : '2. Carregar Planilha de Logística (Opcional)'}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    Aceita **XLS/XLSX/CSV** com **PLACA** e **Número NF**
                                                </div>
                                            </div>
                                        </div>
                                        <input
                                            type="file"
                                            accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/csv"
                                            onChange={handleLogisticaUpload}
                                            className="hidden"
                                            disabled={groups.length === 0}
                                        />
                                    </label>
                                </div>
                            </div>

                            <div className="lg:col-span-1">
                                <QuickCalculator />
                            </div>
                        </div>

                        {/* Lista de Grupos */}
                        <div className="space-y-6 pt-4">
                            {groups.length === 0 && !loading && (
                                <div className="text-center text-gray-500 p-12 bg-white rounded-xl border border-gray-200 shadow-md">
                                    <Package className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                                    <p className="text-lg font-medium">Aguardando a importação dos XMLs...</p>
                                </div>
                            )}
                            {groups.map((g) => {
                                const colorClasses = getCompanyColorClass(g.emitFant);
                                const borderColor = getCompanyBorderColor(g.emitFant);
                                const textColor = colorClasses.split(' ').find(c => c.startsWith('text-'));

                                return (
                                    <div key={g.key} className={`rounded-xl border ${borderColor} overflow-hidden shadow-lg transition duration-200 hover:shadow-xl`}>
                                        <div
                                            className={`flex flex-col md:flex-row items-start md:items-center justify-between p-4 ${colorClasses.includes('from-') ? 'bg-gradient-to-r' : ''} ${colorClasses} bg-opacity-70 cursor-pointer`}
                                            onClick={() => toggleGroup(g.key)}
                                        >
                                            <div className="flex items-center gap-3 w-full md:w-auto">
                                                <button
                                                    className={`p-1.5 rounded-full bg-white/40 ${textColor.replace('text-', 'text-')} hover:bg-white/70 transition flex-shrink-0`}
                                                    aria-label={g.open ? 'Fechar Grupo' : 'Abrir Grupo'}
                                                    onClick={(e) => { e.stopPropagation(); toggleGroup(g.key); }}
                                                >
                                                    {g.open ? <ChevronUp className='w-5 h-5' /> : <ChevronDown className='w-5 h-5' />}
                                                </button>
                                                <div>
                                                    <div className={`text-xl font-extrabold ${textColor}`}>
                                                        {g.emitFant || g.emitName}
                                                        <span className="text-sm font-medium text-gray-600 ml-2">
                                                            / {g.city}{g.uf ? ` - ${g.uf}` : ''}
                                                        </span>
                                                    </div>
                                                    <div className="text-sm text-gray-700 font-medium mt-1">
                                                        <span className="font-bold">{g.totals.totalNotes}</span> Notas •
                                                        <span className="font-bold"> {g.totals.totalItems}</span> Itens
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex-shrink-0 text-right mt-3 md:mt-0 border-l pl-4 border-gray-300">
                                                <div className="text-sm text-gray-600">Peso Bruto Total</div>
                                                <div className={`text-2xl font-extrabold ${textColor}`}>{Number(g.totals.totalPesoB || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 })} kg</div>
                                                <div className="text-xs text-gray-600">
                                                    {g.totals.totalQVol} volumes • R$ {Number(g.totals.totalVNF || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                </div>
                                            </div>
                                        </div>

                                        {g.open && (
                                            <div className="bg-white p-4 sm:p-6 border-t border-gray-200">
                                                <div className="space-y-4">
                                                    {g.notes.map((n, ni) => (
                                                        <div key={n.nNF + ni} className="border border-gray-200 rounded-lg shadow-md overflow-hidden bg-white">
                                                            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between p-3 bg-gray-50 border-b border-gray-200">
                                                                <div className="flex-grow min-w-0">
                                                                    <div className="text-lg font-bold text-gray-800">
                                                                        NF: <span className="text-blue-700">{n.nNF}</span>
                                                                        <span className="text-sm font-normal text-gray-500 ml-2">({n.dhEmi})</span>
                                                                    </div>
                                                                    <div className="text-xs text-gray-600 mt-1">
                                                                        <span className="font-medium">Destinatário:</span> {n.destName} |
                                                                        <span className="font-medium"> Transp:</span> {n.transportName || 'Não Informado'}
                                                                    </div>
                                                                    <div className="text-sm text-gray-700 mt-2">
                                                                        <span className="font-medium">Placa do Veículo:</span>
                                                                        <span className={`font-bold ml-1 ${n.Placa ? 'text-indigo-600' : 'text-red-500'}`}>
                                                                            {n.Placa || 'NÃO INFORMADA'}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-3 lg:mt-0 lg:ml-4">
                                                                    <div className="flex items-center gap-2 w-full sm:w-auto">
                                                                        <label htmlFor={`observacao-${g.key}-${ni}`} className="text-sm font-medium text-gray-700 flex-shrink-0">
                                                                            Observação:
                                                                        </label>
                                                                        <input
                                                                            id={`observacao-${g.key}-${ni}`}
                                                                            type="text"
                                                                            value={n.Observacao || ''}
                                                                            onChange={(ev) => handleObservacaoChange(g.key, ni, ev.target.value)}
                                                                            placeholder="Detalhes da conferência..."
                                                                            className="border border-gray-300 rounded-lg px-2 py-1 text-sm shadow-inner focus:border-blue-500 focus:ring-1 focus:ring-blue-500 w-full sm:w-48"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        />
                                                                    </div>
                                                                    
                                                                    <div className="text-right flex-shrink-0 hidden sm:block">
                                                                        <div className="text-sm text-gray-500">Peso (NF)</div>
                                                                        <div className="font-bold text-gray-800">
                                                                            {n.pesoB ? Number(n.pesoB).toLocaleString('pt-BR', { maximumFractionDigits: 3 }) : '0'} kg
                                                                        </div>
                                                                        <div className="text-xs text-gray-500">Volumes: {n.qVol}</div> 
                                                                    </div>

                                                                    <button
                                                                        onClick={() => toggleNoteOpen(g.key, ni)}
                                                                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition duration-150
                                                                            ${n.open ? `bg-${PRIMARY_COLOR} text-white hover:bg-${PRIMARY_HOVER} shadow-md` : `bg-white text-${PRIMARY_COLOR} border border-${PRIMARY_COLOR} hover:bg-gray-50`} flex-shrink-0`}
                                                                    >
                                                                        {n.open ? 'Ocultar Itens' : 'Conferir Itens'}
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            {n.open && (
                                                                <div className="p-4">
                                                                    <div className="overflow-x-auto border rounded-lg">
                                                                        <table className="min-w-full text-sm divide-y divide-gray-200">
                                                                            <thead className="bg-slate-100 sticky top-0">
                                                                                <tr className="text-left text-gray-700 uppercase tracking-wider">
                                                                                    <th className="px-3 py-2 font-bold whitespace-nowrap">Cód.</th>
                                                                                    <th className="px-3 py-2 font-bold min-w-[200px]">Produto</th>
                                                                                    <th className="px-3 py-2 font-bold text-center whitespace-nowrap">Qtd XML</th>
                                                                                    <th className="px-3 py-2 font-bold text-center">Un</th>
                                                                                    <th className="px-3 py-2 font-bold text-right whitespace-nowrap">Valor Total (R$)</th>
                                                                                    <th className="px-3 py-2 font-bold text-center min-w-[120px]">Qtd Conferida</th>
                                                                                    <th className="px-3 py-2 font-bold text-center whitespace-nowrap">Status</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody className="divide-y divide-gray-100">
                                                                                {n.items.map((it, ii) => {
                                                                                    let statusClass = 'text-slate-500';
                                                                                    if (it.Status === 'OK') statusClass = 'text-emerald-700 bg-emerald-100 font-semibold';
                                                                                    if (it.Status === 'Divergente') statusClass = 'text-red-700 bg-red-100 font-semibold';

                                                                                    // Mostrar o código limpo na tela
                                                                                    const cleanCProdTela = (it.cProd || '').toString().replace(/\D/g, '');

                                                                                    return (
                                                                                        <tr key={it.cProd + ii} className="hover:bg-gray-50">
                                                                                            <td className="px-3 py-3 align-top whitespace-nowrap text-gray-600">{cleanCProdTela || '-'}</td>
                                                                                            <td className="px-3 py-3 align-top text-gray-800">
                                                                                                {it.xProd || '-'}
                                                                                                {it.infAdProd && (
                                                                                                    <div className="text-xs text-blue-500 mt-1 italic font-medium">
                                                                                                        Qtde_aux: {it.infAdProd}
                                                                                                    </div>
                                                                                                )}
                                                                                                {(it.pesoBItem > 0 || it.pesoLItem > 0) && (
                                                                                                    <div className="text-xs text-gray-500 mt-1">
                                                                                                        Peso Item: B: {it.pesoBItem.toLocaleString('pt-BR')}kg | L: {it.pesoLItem.toLocaleString('pt-BR')}kg
                                                                                                    </div>
                                                                                                )}
                                                                                            </td>
                                                                                            <td className="px-3 py-3 align-top text-center font-medium text-gray-700 whitespace-nowrap">{it.qCom || '-'}</td>
                                                                                            <td className="px-3 py-3 align-top text-center text-gray-600">{it.uCom || '-'}</td>
                                                                                            <td className="px-3 py-3 align-top text-right font-medium text-gray-700 whitespace-nowrap">
                                                                                                {it.vProd ? Number(it.vProd).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '-'}
                                                                                            </td>
                                                                                            <td className="px-3 py-2 align-top w-40">
                                                                                                <input
                                                                                                    type="number"
                                                                                                    value={it.Quantidade_Conferida || ''}
                                                                                                    onChange={(ev) => handleQuantidadeChange(g.key, ni, ii, ev.target.value)}
                                                                                                    className="w-full border border-gray-300 rounded-lg px-2 py-1 text-center text-sm shadow-inner focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                                                                                    placeholder="0"
                                                                                                    min="0"
                                                                                                />
                                                                                            </td>
                                                                                            <td className={`px-3 py-2 align-middle text-center whitespace-nowrap ${statusClass} rounded-r-lg`}>
                                                                                                {it.Status === 'OK' ? 'CONFERIDO' : it.Status === 'DIVERGENTE' ? 'DIVERGENTE' : 'PENDENTE'}
                                                                                            </td>
                                                                                        </tr>
                                                                                    );
                                                                                })}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* -------------------- MODO: CONSULTAR BATE CARGA -------------------- */}
                {view === 'consultar' && (
                    <div className="bg-white rounded-xl shadow-2xl p-6 border border-gray-100">
                        <div className="flex items-center gap-4 mb-6">
                            <div className={`p-3 bg-white rounded-full border border-${PRIMARY_COLOR} flex-shrink-0`}>
                                <Search className={`w-6 h-6 text-${PRIMARY_COLOR}`} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-gray-800">Consultar Bate Cargas Gerados</h2>
                                <p className="text-sm text-gray-600 mt-1">Pesquise por data para ver o histórico do Supabase.</p>
                            </div>
                        </div>

                        {error && (
                            <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-400 text-red-800 rounded-lg flex items-start gap-2">
                                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                <span className='whitespace-pre-line text-sm'>{error}</span>
                            </div>
                        )}

                        <div className="flex flex-col sm:flex-row items-end gap-4 mb-8">
                            <div className="w-full sm:w-auto">
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Data da Importação</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Calendar className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input 
                                        type="date" 
                                        className="pl-10 w-full sm:w-64 border border-gray-300 rounded-lg px-4 py-2 shadow-sm focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                                        value={consultaDataStr}
                                        onChange={(e) => setConsultaDataStr(e.target.value)}
                                    />
                                </div>
                            </div>
                            <button 
                                onClick={handleConsultar}
                                disabled={consultaLoading}
                                className={`w-full sm:w-auto flex items-center justify-center gap-2 bg-${PRIMARY_COLOR} hover:bg-${PRIMARY_HOVER} text-white px-6 py-2 rounded-lg font-bold transition duration-200 shadow-md disabled:opacity-50`}
                            >
                                {consultaLoading ? 'Buscando...' : 'Pesquisar Notas'}
                            </button>
                        </div>

                        {/* Tabela de Resultados da Consulta */}
                        {consultaResultados.length > 0 ? (
                            <div className="overflow-x-auto border border-gray-200 rounded-xl">
                                <table className="min-w-full text-sm divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr className="text-left text-gray-600 uppercase tracking-wider text-xs">
                                            <th className="px-4 py-3 font-bold">Data Importação</th>
                                            <th className="px-4 py-3 font-bold">Nota Fiscal</th>
                                            <th className="px-4 py-3 font-bold">Destinatário</th>
                                            <th className="px-4 py-3 font-bold text-center">Placa</th>
                                            <th className="px-4 py-3 font-bold text-right">Peso Total</th>
                                            <th className="px-4 py-3 font-bold text-center">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-100">
                                        {consultaResultados.map((res) => (
                                            <React.Fragment key={res.id}>
                                                <tr className="hover:bg-amber-50 transition">
                                                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                                                        {new Date(res.data_importacao).toLocaleDateString('pt-BR')} {new Date(res.data_importacao).toLocaleTimeString('pt-BR', {hour: '2-digit', minute:'2-digit'})}
                                                    </td>
                                                    <td className="px-4 py-3 font-bold text-gray-800">{res.numero_nf}</td>
                                                    <td className="px-4 py-3 text-gray-700">{res.destinatario}</td>
                                                    <td className="px-4 py-3 text-center">
                                                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${res.placa_veiculo ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                                                            {res.placa_veiculo || 'S/N'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-right font-medium text-gray-700 whitespace-nowrap">
                                                        {Number(res.peso_bruto_total || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 })} kg
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <button 
                                                            onClick={() => toggleNfDetails(res.id)}
                                                            className={`flex items-center justify-center gap-1 mx-auto px-3 py-1 rounded-full text-xs font-bold transition-colors ${expandedNfId === res.id ? `bg-${PRIMARY_COLOR} text-white` : `text-${PRIMARY_COLOR} hover:bg-amber-100`}`}
                                                        >
                                                            {expandedNfId === res.id ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                                            {expandedNfId === res.id ? 'Fechar' : 'Detalhes'}
                                                        </button>
                                                    </td>
                                                </tr>
                                                
                                                {/* Área Expandida com os Detalhes dos Itens */}
                                                {expandedNfId === res.id && (
                                                    <tr>
                                                        <td colSpan="6" className="p-4 bg-gray-50 border-b border-gray-200">
                                                            {loadingItems ? (
                                                                <div className="text-center text-sm text-gray-500 py-4 flex items-center justify-center gap-2">
                                                                    <div className={`w-2 h-2 bg-${PRIMARY_COLOR} rounded-full animate-pulse`}></div>
                                                                    Carregando itens da nota...
                                                                </div>
                                                            ) : nfItems.length > 0 ? (
                                                                <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-sm">
                                                                    <table className="min-w-full text-xs divide-y divide-gray-200">
                                                                        <thead className="bg-slate-100">
                                                                            <tr className="text-left text-gray-600 uppercase tracking-wider">
                                                                                <th className="px-3 py-2 font-bold">Cód.</th>
                                                                                <th className="px-3 py-2 font-bold">Produto</th>
                                                                                <th className="px-3 py-2 font-bold text-center">Qtd XML</th>
                                                                                <th className="px-3 py-2 font-bold text-center">Qtd Conferida</th>
                                                                                <th className="px-3 py-2 font-bold text-center">Status</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody className="divide-y divide-gray-100">
                                                                            {nfItems.map((item) => {
                                                                                let statusClass = 'text-slate-500';
                                                                                if (item.status_conferencia === 'OK') statusClass = 'text-emerald-700 bg-emerald-100 font-semibold';
                                                                                if (item.status_conferencia === 'Divergente' || item.status_conferencia === 'DIVERGENTE') statusClass = 'text-red-700 bg-red-100 font-semibold';
                                                                                
                                                                                return (
                                                                                    <tr key={item.id} className="hover:bg-gray-50">
                                                                                        <td className="px-3 py-2 whitespace-nowrap text-gray-600">{item.codigo_produto}</td>
                                                                                        <td className="px-3 py-2 text-gray-800">
                                                                                            {item.descricao_produto} 
                                                                                            {item.qtde_aux && <span className="text-xs text-blue-500 italic block mt-0.5">Qtde_aux: {item.qtde_aux}</span>}
                                                                                        </td>
                                                                                        <td className="px-3 py-2 text-center text-gray-700 font-medium">{item.qtd_xml}</td>
                                                                                        <td className="px-3 py-2 text-center text-gray-700 font-medium">{item.qtd_conferida}</td>
                                                                                        <td className={`px-3 py-2 text-center whitespace-nowrap ${statusClass}`}>
                                                                                            {item.status_conferencia === 'OK' ? 'CONFERIDO' : item.status_conferencia === 'DIVERGENTE' ? 'DIVERGENTE' : 'PENDENTE'}
                                                                                        </td>
                                                                                    </tr>
                                                                                )
                                                                            })}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            ) : (
                                                                <div className="text-center text-sm text-gray-500 py-4">Nenhum item encontrado no banco de dados para esta nota.</div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            !consultaLoading && consultaDataStr && (
                                <div className="text-center py-10 text-gray-500 border-2 border-dashed border-gray-200 rounded-xl">
                                    <List className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                                    <p>Nenhuma nota encontrada no Supabase para a data selecionada.</p>
                                </div>
                            )
                        )}
                    </div>
                )}
            </div>
            
            {/* RODAPÉ */}
            <footer className="bg-white text-gray-600 p-4 mt-8 flex-shrink-0 border-t border-gray-200">
                <div className="max-w-7xl mx-auto text-center text-xs">
                    <p>
                        &copy; {new Date().getFullYear()} VnTech Solutions. Todos os direitos reservados.
                    </p>
                    <p className={`mt-1 text-${PRIMARY_COLOR}`}>
                        Desenvolvido por VnTech Dev Team.
                    </p>
                </div>
            </footer>
        </div>
    );
}